import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useNavigate } from "react-router-dom";
import { useOnvifScanStore } from "./onvifScanStore/onvifScanStore";
import { startCameraUnitScan } from "./OnvifScanUtils/startCameraUnitScan";
import { applyCameraUnitScan } from "./OnvifScanUtils/applyCameraUnitScan";
import {
  buildUnitScanRows,
  countSelected,
  describeCredentialRef,
} from "./OnvifScanUtils/cameraUnitScan";
import { CameraUnitScanResultsTable } from "./CameraUnitScanResultsTable";

const headingSx = {
  fontFamily: "Mulish",
  fontWeight: 700,
  color: "#001943",
};

export const UNIT_SCAN_EXPLANATION =
  "Reads every camera's unit over ONVIF using the site's camera login, and " +
  "updates host, streaming port, stream path and login on the existing cameras.";

const SUMMARY_LABELS: { key: string; label: string }[] = [
  { key: "hosts", label: "hosts" },
  { key: "reachable", label: "reachable" },
  { key: "authenticated", label: "logged in" },
  { key: "with_substream", label: "with substream" },
  { key: "up_to_date", label: "up to date" },
  { key: "will_change", label: "will change" },
  { key: "failed", label: "failed" },
];

/**
 * "Update existing cameras" mode of the scan page. No WireGuard tunnel, no
 * subnet: Camera Admin probes the host behind every camera the site already
 * has (first users: Genetec sites with video source = direct over IPsec) and
 * proposes the fields to rewrite. The operator picks the cameras and applies.
 */
export const CameraUnitScanPanel = ({
  siteId,
  onSuccess,
}: {
  siteId: string;
  onSuccess?: () => void;
}) => {
  const navigate = useNavigate();
  const {
    credentials,
    setCredentials,
    scanStatus,
    errorMessage,
    taskId,
    unitScanTargets,
    unitScanCredentialRefs,
    unitScanResult,
    unitSelectedIds,
    setUnitSelectedIds,
    applying,
    applyResult,
  } = useOnvifScanStore();

  const scanning = scanStatus === "pending";
  const rows = React.useMemo(
    () => buildUnitScanRows(unitScanResult),
    [unitScanResult]
  );
  const selectedCount = countSelected(unitSelectedIds);
  const summary = unitScanResult?.summary ?? {};
  const credentialRefs =
    unitScanResult?.credentials && unitScanResult.credentials.length > 0
      ? unitScanResult.credentials
      : unitScanCredentialRefs;

  const updateCredential = (
    index: number,
    field: "username" | "password",
    value: string
  ) => {
    setCredentials(
      credentials.map((c, i) => (i === index ? { ...c, [field]: value } : c))
    );
  };
  const addCredentialRow = () =>
    setCredentials([...credentials, { username: "", password: "" }]);
  const removeCredentialRow = (index: number) =>
    setCredentials(credentials.filter((_, i) => i !== index));

  const handleScan = () => {
    if (!siteId) {
      return;
    }
    startCameraUnitScan({ siteId, credentials, navigate });
  };

  const toggle = (id: number) =>
    setUnitSelectedIds({ ...unitSelectedIds, [id]: !unitSelectedIds[id] });

  const toggleAll = (checked: boolean) => {
    const next: Record<number, boolean> = {};
    rows.forEach((row) => {
      if (row.canApply) {
        next[row.id] = checked;
      }
    });
    setUnitSelectedIds(next);
  };

  const handleApply = () => {
    if (!taskId) {
      return;
    }
    applyCameraUnitScan({
      taskId,
      siteId,
      selectedIds: unitSelectedIds,
      navigate,
      onSuccess,
    });
  };

  const busy = scanning || applying;

  return (
    <Box sx={{ p: 3, maxWidth: 1400 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography sx={{ ...headingSx, fontSize: 22 }}>
          Refresh camera streams
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate(`/sites/aboutsite/${siteId}`)}
          disabled={busy}
          sx={{
            fontFamily: "Mulish",
            textTransform: "none",
            backgroundColor: "#283E6D",
            color: "#FFFFFF",
            "&:hover": { backgroundColor: "#1f2f54" },
          }}
        >
          Back to site
        </Button>
      </Box>

      <Paper sx={{ p: 2.5, mb: 3 }}>
        <Typography sx={{ fontFamily: "Mulish", color: "#283E6D", mb: 2 }}>
          {UNIT_SCAN_EXPLANATION}
        </Typography>

        <Typography sx={{ ...headingSx, fontSize: 16, mb: 0.5 }}>
          Extra logins (optional)
        </Typography>
        <Typography
          sx={{ fontFamily: "Mulish", color: "#7A7A7A", fontSize: 13, mb: 1 }}
        >
          The site&apos;s camera login and each camera&apos;s own login are
          always tried. Add more here only if some units use a different one.
        </Typography>
        <Stack spacing={1} sx={{ mb: 1 }}>
          {credentials.map((cred, index) => (
            <Stack direction="row" spacing={1} key={index} alignItems="center">
              <TextField
                size="small"
                label="Username"
                value={cred.username}
                onChange={(e) =>
                  updateCredential(index, "username", e.target.value)
                }
                disabled={busy}
                sx={{ fontFamily: "Mulish" }}
              />
              <TextField
                size="small"
                label="Password"
                type="password"
                value={cred.password}
                onChange={(e) =>
                  updateCredential(index, "password", e.target.value)
                }
                disabled={busy}
                sx={{ fontFamily: "Mulish" }}
              />
              <IconButton
                aria-label="remove credential"
                onClick={() => removeCredentialRow(index)}
                disabled={credentials.length <= 1 || busy}
                size="small"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
        <Button
          startIcon={<AddIcon />}
          onClick={addCredentialRow}
          disabled={busy}
          sx={{ fontFamily: "Mulish", textTransform: "none", display: "block" }}
        >
          Add login
        </Button>
      </Paper>

      <Button
        variant="contained"
        onClick={handleScan}
        disabled={!siteId || busy}
        sx={{
          fontFamily: "Mulish",
          textTransform: "none",
          backgroundColor: "#FF8400",
          "&:hover": { backgroundColor: "#E67300" },
        }}
      >
        {scanning ? "Scanning…" : "Scan"}
      </Button>

      {scanning && (
        <Stack spacing={1} sx={{ mt: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={22} />
            <Typography sx={{ fontFamily: "Mulish", color: "#283E6D" }}>
              Reading{" "}
              {unitScanTargets != null
                ? `${unitScanTargets} camera unit${
                    unitScanTargets === 1 ? "" : "s"
                  }`
                : "camera units"}{" "}
              over ONVIF. This can take up to a few minutes…
            </Typography>
          </Stack>
          {credentialRefs.length > 0 && (
            <Typography
              sx={{ fontFamily: "Mulish", color: "#7A7A7A", fontSize: 13 }}
            >
              Logins tried:{" "}
              {credentialRefs.map(describeCredentialRef).join(", ")}
            </Typography>
          )}
        </Stack>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ fontFamily: "Mulish", mt: 3 }}>
          {errorMessage}
        </Alert>
      )}

      {applyResult && (
        <Alert
          severity={applyResult.skipped.length > 0 ? "warning" : "success"}
          sx={{ fontFamily: "Mulish", mt: 3 }}
        >
          Updated {applyResult.updated.length} camera
          {applyResult.updated.length === 1 ? "" : "s"}
          {applyResult.updated.length > 0
            ? `: ${applyResult.updated.map((u) => u.name).join(", ")}`
            : ""}
          .
          {applyResult.skipped.length > 0 && (
            <div style={{ marginTop: 6 }}>
              Skipped {applyResult.skipped.length}:{" "}
              {applyResult.skipped
                .map((s) => `camera ${s.camera_id} (${s.reason})`)
                .join(", ")}
            </div>
          )}
        </Alert>
      )}

      {scanStatus === "success" && unitScanResult && (
        <Box sx={{ mt: 3 }}>
          {/* Summary strip */}
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 2 }}
            data-testid="unit-scan-summary"
          >
            {SUMMARY_LABELS.filter(
              ({ key }) =>
                (summary as Record<string, number | undefined>)[key] != null
            ).map(({ key, label }) => (
              <Chip
                key={key}
                size="small"
                label={`${
                  (summary as Record<string, number | undefined>)[key]
                } ${label}`}
                sx={{
                  fontFamily: "Mulish",
                  backgroundColor:
                    key === "will_change"
                      ? "#FFF3E0"
                      : key === "failed"
                      ? "#FDECEA"
                      : "#EEF2F8",
                  color: "#283E6D",
                }}
              />
            ))}
          </Stack>
          {credentialRefs.length > 0 && (
            <Typography
              sx={{
                fontFamily: "Mulish",
                color: "#7A7A7A",
                fontSize: 13,
                mb: 1,
              }}
            >
              Logins tried:{" "}
              {credentialRefs.map(describeCredentialRef).join(", ")}
            </Typography>
          )}

          {rows.length === 0 ? (
            <Alert severity="info" sx={{ fontFamily: "Mulish" }}>
              The scan returned no cameras. Only cameras with a host are
              scanned.
            </Alert>
          ) : (
            <>
              <Typography sx={{ ...headingSx, fontSize: 16, mb: 1 }}>
                {rows.length} camera{rows.length === 1 ? "" : "s"}
                {selectedCount > 0 ? ` — ${selectedCount} selected` : ""}
              </Typography>
              <CameraUnitScanResultsTable
                rows={rows}
                selectedIds={unitSelectedIds}
                onToggle={toggle}
                onToggleAll={toggleAll}
                disabled={applying}
              />
              <Button
                variant="contained"
                onClick={handleApply}
                disabled={selectedCount === 0 || busy || !taskId}
                sx={{
                  fontFamily: "Mulish",
                  textTransform: "none",
                  mt: 2,
                  backgroundColor: "#FF8400",
                  "&:hover": { backgroundColor: "#E67300" },
                }}
              >
                {applying
                  ? "Applying…"
                  : `Apply to ${selectedCount} camera${
                      selectedCount === 1 ? "" : "s"
                    }`}
              </Button>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};
